import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { Bell, Sun, Moon, Menu, X, Search, DollarSign, FileText, AlertCircle, CheckCircle, MessageSquare } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = ({ onMenuClick, menuOpen, onSearchClick }) => {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      // Phase D — merge legacy system notifications with portal-push notifications
      // (comm requests, mass-quote alerts, etc.) so the bell shows everything.
      const [legacy, portal] = await Promise.allSettled([
        axios.get(`${API}/notifications`),
        axios.get(`${API}/notifications/portal`),
      ]);
      const legacyList = legacy.status === "fulfilled" ? (legacy.value.data || []) : [];
      const portalRaw = portal.status === "fulfilled" ? (portal.value.data?.notifications || []) : [];
      // Normalize portal_push shape → legacy shape for rendering parity
      const portalList = portalRaw.map((n) => ({
        id: n.id,
        _portal: true,
        title: n.title,
        message: n.body,
        action_url: n.link || null,
        category: n.kind || "general",
        notification_type: n.kind === "comm_request" ? "info" : "info",
        is_read: !!n.read,
        created_at: n.created_at,
      }));
      const merged = [...legacyList, ...portalList].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setNotifications(merged);
      setUnreadCount(merged.filter((n) => !n.is_read).length);
    } catch (error) {
      // Silently fail - notifications are non-critical
    }
  };

  const markAsRead = async (notif, actionUrl) => {
    try {
      if (notif._portal) {
        await axios.put(`${API}/notifications/portal/${notif.id}/read`);
      } else {
        await axios.post(`${API}/notifications/${notif.id}/read`);
      }
      fetchNotifications();
      if (actionUrl) {
        navigate(actionUrl);
      }
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await Promise.allSettled([
        axios.post(`${API}/notifications/mark-all-read`),
        axios.put(`${API}/notifications/portal/read-all`),
      ]);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
    }
  };

  const getNotificationIcon = (type, category) => {
    if (category === "comm_request") return <MessageSquare className="h-4 w-4 text-emerald-500" />;
    if (category === "payment") return <DollarSign className="h-4 w-4 text-emerald-500" />;
    if (category === "invoice") return <FileText className="h-4 w-4 text-blue-500" />;
    if (type === "success") return <CheckCircle className="h-4 w-4 text-emerald-500" />;
    if (type === "warning") return <AlertCircle className="h-4 w-4 text-amber-500" />;
    return <Bell className="h-4 w-4 text-muted-foreground" />;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <header className="sticky top-0 z-20 w-full border-b bg-background/80 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between px-4 md:px-6">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onMenuClick}
          data-testid="mobile-menu-btn"
        >
          {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>

        {/* Search Bar - Desktop */}
        <button
          onClick={onSearchClick}
          className="hidden md:flex items-center gap-2 h-9 px-4 text-sm text-muted-foreground bg-muted/50 hover:bg-muted rounded-lg border border-input transition-colors"
          data-testid="search-trigger"
        >
          <Search className="h-4 w-4" />
          <span>Search...</span>
          <kbd className="ml-8 pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-background px-1.5 font-mono text-[10px] font-medium opacity-100">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>

        {/* Right side actions */}
        <div className="flex items-center gap-2">
          {/* Search - Mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onSearchClick}
            data-testid="mobile-search-btn"
          >
            <Search className="h-5 w-5" />
          </Button>

          {/* Theme toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle-btn"
          >
            {theme === "dark" ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                data-testid="notifications-btn"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <Badge
                    className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
                    variant="destructive"
                  >
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-96">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <span className="font-semibold">Notifications</span>
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                    className="text-xs h-7"
                  >
                    Mark all read
                  </Button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="px-4 py-12 text-center">
                    <Bell className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No notifications yet</p>
                    <p className="text-xs text-muted-foreground/70">You'll see payment alerts and updates here</p>
                  </div>
                ) : (
                  notifications.slice(0, 15).map((notif, index) => (
                    <div key={notif.id}>
                      <DropdownMenuItem
                        onClick={() => markAsRead(notif, notif.action_url)}
                        className={`flex items-start gap-3 p-4 cursor-pointer ${
                          !notif.is_read ? "bg-accent/50" : ""
                        }`}
                      >
                        <div className="mt-0.5">
                          {getNotificationIcon(notif.notification_type, notif.category)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm leading-tight ${!notif.is_read ? "font-medium" : ""}`}>
                            {notif.title}
                          </p>
                          {notif.message && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {notif.message}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground/70 mt-1">
                            {formatTime(notif.created_at)}
                          </p>
                        </div>
                        {!notif.is_read && (
                          <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
                        )}
                      </DropdownMenuItem>
                      {index < notifications.length - 1 && <DropdownMenuSeparator className="m-0" />}
                    </div>
                  ))
                )}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};

export default Header;
